import { useEffect, useState } from 'react';

import type { Collection } from '@/types/collection';

import { api } from '@/lib/api';

/**
 * TITLES ALREADY FETCHED, so a crumb never starts at "…" for a collection we have named before.
 *
 * Module-level and never evicted: a title is a few bytes, the set is the collections one teacher
 * opens in a session, and the whole point is that it survives the unmount a navigation performs.
 */
const KNOWN = new Map<string, string>();

/**
 * A collection's title by id, for the breadcrumbs at the experience and task levels.
 *
 * ## A FAILED FETCH USED TO LOOK EXACTLY LIKE A PENDING ONE
 *
 * `null` was both "not back yet" and "it failed", and the crumb draws `…` for `null` — so a
 * request that lost a race, or hit the window where a token is being refreshed, left the
 * breadcrumb showing an ellipsis for ever. The effect's only dependency is the id, so nothing
 * ever asked again. Reported as "sometimes the breadcrumbs don't resolve in experience builder
 * (collection becomes ...)".
 *
 * Two changes, and the first is what makes the second affordable:
 *
 *   - the title is CACHED, so the second visit to a collection paints its name on the first
 *     frame rather than flashing `…` while a round trip happens again;
 *   - a failure RETRIES, twice, backing off. A transient failure is the whole reported symptom
 *     and one more attempt costs nothing against a title endpoint.
 *
 * It still revalidates in the background when the cache has an entry, so renaming a collection
 * reaches the crumb without a reload — the cached value is a head start, not an answer.
 */
export function useCollectionTitle(collectionId?: string | null): string | null {
  const [title, setTitle] = useState<string | null>(
    () => (collectionId ? KNOWN.get(collectionId) ?? null : null));

  useEffect(() => {
    if (!collectionId) { setTitle(null); return; }
    // Synchronously, before any await: this is the frame the crumb would otherwise draw `…` in.
    const cached = KNOWN.get(collectionId);
    if (cached !== undefined) setTitle(cached);

    let alive = true;
    let timer = 0;
    let attempt = 0;
    const load = () => {
      api.get<Collection>(`/collections/${collectionId}`)
        .then((c) => {
          KNOWN.set(collectionId, c.title);
          if (alive) setTitle(c.title);
        })
        .catch(() => {
          if (!alive) return;
          attempt += 1;
          /* Two more tries, 400ms apart. Beyond that the collection is genuinely unreadable —
             deleted, or not ours — and `null` is then the honest answer rather than a guess. */
          if (attempt <= 2) { timer = window.setTimeout(load, attempt * 400); return; }
          if (cached === undefined) setTitle(null);
        });
    };
    load();
    return () => { alive = false; window.clearTimeout(timer); };
  }, [collectionId]);

  return title;
}
